import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, FileText } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import CurrentAssetsSection from './CurrentAssetsSection';
import CurrentLiabilitiesSection from './CurrentLiabilitiesSection';
import LongTermAssetsSection from './LongTermAssetsSection';
import LongTermLiabilitiesSection from './LongTermLiabilitiesSection';
import SettingsSection from './SettingsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isNumber } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета коэффициентов ликвидности
 */
const CalculationForm = ({
  formData,
  setFormData,
  errors,
  setErrors,
  isCalculating,
  setIsCalculating,
  results,
  setResults,
  industryTypes,
  companySizes,
  saveReport
}) => {
  // Состояние для имени сохраняемого отчета
  const [saveReportName, setSaveReportName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = 0) => {
    if (val === undefined || val === null || val === '') return defaultVal;
    const parsed = parseFloat(String(val).replace(/\s+/g, '').replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    // Определяем, к какой категории относится поле
    if (name.startsWith('currentAssets.')) {
      const assetField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        currentAssets: {
          ...prev.currentAssets,
          [assetField]: fieldValue
        }
      }));
    } else if (name.startsWith('currentLiabilities.')) {
      const liabilityField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        currentLiabilities: {
          ...prev.currentLiabilities,
          [liabilityField]: fieldValue
        }
      }));
    } else if (name.startsWith('longTermAssets.')) {
      const assetField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        longTermAssets: {
          ...prev.longTermAssets,
          [assetField]: fieldValue
        }
      }));
    } else if (name.startsWith('longTermLiabilities.')) {
      const liabilityField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        longTermLiabilities: {
          ...prev.longTermLiabilities,
          [liabilityField]: fieldValue
        }
      }));
    } else if (name.startsWith('settings.')) {
      const settingField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          [settingField]: fieldValue
        }
      }));
    } else {
      // Если поле верхнего уровня
      setFormData(prev => ({ ...prev, [name]: fieldValue }));
    }

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    
    // Валидация оборотных активов
    Object.entries(formData.currentAssets).forEach(([key, value]) => {
      const fieldName = `currentAssets.${key}`;
      const numValue = parseFloatOrDefault(value, -1);
      
      if (!isNumber(value)) {
        newErrors[fieldName] = 'Введите корректное число';
      } else if (numValue < 0) {
        newErrors[fieldName] = 'Значение не может быть отрицательным';
      }
    });
    
    // Валидация краткосрочных обязательств
    Object.entries(formData.currentLiabilities).forEach(([key, value]) => {
      const fieldName = `currentLiabilities.${key}`;
      const numValue = parseFloatOrDefault(value, -1);
      
      if (!isNumber(value)) {
        newErrors[fieldName] = 'Введите корректное число';
      } else if (numValue < 0) {
        newErrors[fieldName] = 'Значение не может быть отрицательным';
      }
    });
    
    // Валидация долгосрочных активов
    Object.entries(formData.longTermAssets).forEach(([key, value]) => {
      const fieldName = `longTermAssets.${key}`;
      const numValue = parseFloatOrDefault(value, -1);
      
      if (!isNumber(value)) {
        newErrors[fieldName] = 'Введите корректное число';
      } else if (numValue < 0) {
        newErrors[fieldName] = 'Значение не может быть отрицательным';
      }
    });
    
    // Валидация долгосрочных обязательств
    Object.entries(formData.longTermLiabilities).forEach(([key, value]) => {
      const fieldName = `longTermLiabilities.${key}`;
      const numValue = parseFloatOrDefault(value, -1);
      
      if (!isNumber(value)) {
        newErrors[fieldName] = 'Введите корректное число';
      } else if (numValue < 0) {
        newErrors[fieldName] = 'Значение не может быть отрицательным';
      }
    });

    // Проверка, что сумма текущих обязательств не равна нулю (для избежания деления на ноль)
    const totalCurrentLiabilities = Object.values(formData.currentLiabilities)
      .reduce((sum, value) => sum + parseFloatOrDefault(value, 0), 0);
      
    if (totalCurrentLiabilities === 0) {
      newErrors['currentLiabilities'] = 'Сумма краткосрочных обязательств не может быть равна нулю';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета коэффициентов ликвидности
  const calculateLiquidityRatios = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Парсинг данных из формы ---
        
        // Парсинг оборотных активов
        const currentAssets = {
          cash: parseFloatOrDefault(formData.currentAssets.cash),
          shortTermInvestments: parseFloatOrDefault(formData.currentAssets.shortTermInvestments),
          accountsReceivable: parseFloatOrDefault(formData.currentAssets.accountsReceivable),
          inventory: parseFloatOrDefault(formData.currentAssets.inventory),
          vatReceivable: parseFloatOrDefault(formData.currentAssets.vatReceivable),
          otherCurrentAssets: parseFloatOrDefault(formData.currentAssets.otherCurrentAssets)
        };
        
        // Парсинг краткосрочных обязательств
        const currentLiabilities = {
          shortTermLoans: parseFloatOrDefault(formData.currentLiabilities.shortTermLoans),
          accountsPayable: parseFloatOrDefault(formData.currentLiabilities.accountsPayable),
          advancesReceived: parseFloatOrDefault(formData.currentLiabilities.advancesReceived),
          taxesPayable: parseFloatOrDefault(formData.currentLiabilities.taxesPayable),
          otherCurrentLiabilities: parseFloatOrDefault(formData.currentLiabilities.otherCurrentLiabilities)
        };
        
        // Парсинг долгосрочных активов и обязательств
        const longTermAssets = {
          nonCurrentAssets: parseFloatOrDefault(formData.longTermAssets.nonCurrentAssets)
        };
        
        const longTermLiabilities = {
          longTermLoans: parseFloatOrDefault(formData.longTermLiabilities.longTermLoans),
          otherLongTermLiabilities: parseFloatOrDefault(formData.longTermLiabilities.otherLongTermLiabilities)
        };
        
        // --- Расчет коэффициентов ликвидности ---
        
        // Расчет суммарных значений
        const totalCurrentAssets = Object.values(currentAssets).reduce((sum, val) => sum + val, 0);
        const totalCurrentLiabilities = Object.values(currentLiabilities).reduce((sum, val) => sum + val, 0);
        const totalLongTermAssets = Object.values(longTermAssets).reduce((sum, val) => sum + val, 0);
        const totalLongTermLiabilities = Object.values(longTermLiabilities).reduce((sum, val) => sum + val, 0);
        
        // Расчет высоколиквидных активов (денежные средства и краткосрочные финансовые вложения)
        const highlyLiquidAssets = currentAssets.cash + currentAssets.shortTermInvestments;
        
        // Расчет быстроликвидных активов (высоколиквидные + дебиторская задолженность)
        const quickAssets = highlyLiquidAssets + currentAssets.accountsReceivable;
        
        // Расчет коэффициентов ликвидности
        const currentRatio = totalCurrentAssets / totalCurrentLiabilities;
        const quickRatio = quickAssets / totalCurrentLiabilities;
        const cashRatio = highlyLiquidAssets / totalCurrentLiabilities;
        
        // Коэффициент общей ликвидности (платежеспособности)
        const totalAssets = totalCurrentAssets + totalLongTermAssets;
        const totalLiabilities = totalCurrentLiabilities + totalLongTermLiabilities;
        const generalSolvencyRatio = totalAssets / totalLiabilities;
        
        // Коэффициент обеспеченности собственными оборотными средствами
        const workingCapital = totalCurrentAssets - totalCurrentLiabilities;
        const workingCapitalRatio = workingCapital / totalCurrentAssets;
        
        // Коэффициент маневренности функционирующего капитала
        const equity = totalAssets - totalLiabilities; // Собственный капитал
        const functionalCapital = totalCurrentAssets - currentAssets.inventory;
        const maneuverabilityRatio = functionalCapital > 0 ? equity / functionalCapital : 0;
        
        // Доля оборотных средств в активах
        const currentAssetsToTotalAssetsRatio = totalCurrentAssets / totalAssets;
        
        // Расчет показателей ликвидности баланса
        const a1 = highlyLiquidAssets; // Наиболее ликвидные активы
        const a2 = currentAssets.accountsReceivable; // Быстрореализуемые активы
        const a3 = currentAssets.inventory + currentAssets.vatReceivable + currentAssets.otherCurrentAssets; // Медленно реализуемые активы
        const a4 = totalLongTermAssets; // Труднореализуемые активы
        
        const l1 = currentLiabilities.accountsPayable + currentLiabilities.taxesPayable; // Наиболее срочные обязательства
        const l2 = currentLiabilities.shortTermLoans; // Краткосрочные пассивы
        const l3 = currentLiabilities.advancesReceived + currentLiabilities.otherCurrentLiabilities; // Долгосрочные пассивы
        const l4 = equity; // Постоянные пассивы
        
        const balanceCondition1 = a1 >= l1;
        const balanceCondition2 = a2 >= l2;
        const balanceCondition3 = a3 >= l3;
        const balanceCondition4 = a4 <= l4;
        
        // Оценка риска банкротства на основе модели Альтмана для российских компаний
        // (адаптированная версия Z-score для российского рынка)
        const netSales = totalCurrentAssets * 1.5; // Условно для расчета, можно заменить на реальные данные
        const ebit = totalCurrentAssets * 0.1; // Условно для расчета, можно заменить на реальные данные
        const totalEquity = equity;
        
        const x1 = workingCapital / totalAssets;
        const x2 = (totalCurrentAssets * 0.05) / totalAssets; // Нераспределенная прибыль / Активы (условно)
        const x3 = ebit / totalAssets;
        const x4 = totalEquity / totalLiabilities;
        const x5 = netSales / totalAssets;
        
        // Расчет Z-score (с коэффициентами адаптированными для российского рынка)
        const zScore = 1.2 * x1 + 1.4 * x2 + 3.3 * x3 + 0.6 * x4 + 0.999 * x5;
        
        // Интерпретация Z-score для модели Альтмана
        let bankruptcyRisk;
        if (zScore < 1.81) {
          bankruptcyRisk = {
            level: 'high',
            description: 'Высокий риск банкротства (Z < 1.81)'
          };
        } else if (zScore < 2.77) {
          bankruptcyRisk = {
            level: 'medium',
            description: 'Средний риск банкротства (1.81 < Z < 2.77)'
          };
        } else {
          bankruptcyRisk = {
            level: 'low',
            description: 'Низкий риск банкротства (Z > 2.77)'
          };
        }
        
        // Получение отраслевых нормативов
        const industryStandards = industryTypes.find(
          industry => industry.id === formData.settings.industry
        ) || { currentRatioMin: 2.0, quickRatioMin: 1.0, cashRatioMin: 0.2 };
        
        // Оценка соответствия нормативам
        const currentRatioAssessment = {
          value: currentRatio,
          standard: industryStandards.currentRatioMin,
          meetsStandard: currentRatio >= industryStandards.currentRatioMin,
          status: currentRatio >= industryStandards.currentRatioMin 
            ? 'good' 
            : (currentRatio >= industryStandards.currentRatioMin * 0.8) 
              ? 'warning' 
              : 'danger'
        };
        
        const quickRatioAssessment = {
          value: quickRatio,
          standard: industryStandards.quickRatioMin,
          meetsStandard: quickRatio >= industryStandards.quickRatioMin,
          status: quickRatio >= industryStandards.quickRatioMin 
            ? 'good' 
            : (quickRatio >= industryStandards.quickRatioMin * 0.8) 
              ? 'warning' 
              : 'danger'
        };
        
        const cashRatioAssessment = {
          value: cashRatio,
          standard: industryStandards.cashRatioMin,
          meetsStandard: cashRatio >= industryStandards.cashRatioMin,
          status: cashRatio >= industryStandards.cashRatioMin 
            ? 'good' 
            : (cashRatio >= industryStandards.cashRatioMin * 0.8) 
              ? 'warning' 
              : 'danger'
        };
        
        // Дополнительные рекомендации по улучшению ликвидности
        let recommendations = [];
        
        if (currentRatio < industryStandards.currentRatioMin) {
          recommendations.push({
            ratio: 'currentRatio',
            text: 'Рассмотрите возможность увеличения оборотных активов или снижения краткосрочных обязательств для улучшения коэффициента текущей ликвидности.'
          });
        }
        
        if (quickRatio < industryStandards.quickRatioMin) {
          recommendations.push({
            ratio: 'quickRatio',
            text: 'Работайте над ускорением оборачиваемости дебиторской задолженности и оптимизацией запасов для улучшения коэффициента быстрой ликвидности.'
          });
        }
        
        if (cashRatio < industryStandards.cashRatioMin) {
          recommendations.push({
            ratio: 'cashRatio',
            text: 'Увеличьте долю денежных средств и их эквивалентов для повышения коэффициента абсолютной ликвидности.'
          });
        }
        
        if (workingCapitalRatio < 0.1) {
          recommendations.push({
            ratio: 'workingCapitalRatio',
            text: 'Низкое значение коэффициента обеспеченности собственными оборотными средствами. Рекомендуется увеличить долю собственного капитала в финансировании оборотных активов.'
          });
        }
        
        // --- Подготовка итоговых результатов ---
        const finalResults = {
          // Исходные данные
          totalCurrentAssets,
          totalCurrentLiabilities,
          totalLongTermAssets,
          totalLongTermLiabilities,
          totalAssets,
          totalLiabilities,
          highlyLiquidAssets,
          quickAssets,
          
          // Коэффициенты ликвидности
          currentRatio,
          quickRatio,
          cashRatio,
          generalSolvencyRatio,
          workingCapitalRatio,
          maneuverabilityRatio,
          currentAssetsToTotalAssetsRatio,
          
          // Ликвидность баланса
          balanceLiquidity: {
            a1, a2, a3, a4,
            l1, l2, l3, l4,
            balanceCondition1,
            balanceCondition2,
            balanceCondition3,
            balanceCondition4,
            absoluteLiquidity: balanceCondition1 && balanceCondition2 && balanceCondition3 && balanceCondition4
          },
          
          // Оценка соответствия нормативам
          assessments: {
            currentRatio: currentRatioAssessment,
            quickRatio: quickRatioAssessment,
            cashRatio: cashRatioAssessment
          },
          
          // Риск банкротства
          bankruptcyRisk,
          zScore,
          
          // Рекомендации
          recommendations,
          
          // Метаданные
          industry: formData.settings.industry,
          companySize: formData.settings.companySize,
          date: new Date().toISOString(),
          
          // Указаны ли детальные данные
          hasDetailedData: formData.settings.includeDetailedAssets && formData.settings.includeDetailedLiabilities
        };
        
        setResults(finalResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ 
          general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` 
        });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setIsCalculating, industryTypes]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      // Оборотные активы
      currentAssets: {
        cash: '100000', // Денежные средства
        shortTermInvestments: '50000', // Краткосрочные финансовые вложения
        accountsReceivable: '200000', // Дебиторская задолженность
        inventory: '300000', // Запасы
        vatReceivable: '20000', // НДС по приобретенным ценностям
        otherCurrentAssets: '30000', // Прочие оборотные активы
      },
      
      // Краткосрочные обязательства
      currentLiabilities: {
        shortTermLoans: '150000', // Краткосрочные заемные средства
        accountsPayable: '180000', // Кредиторская задолженность
        advancesReceived: '50000', // Авансы полученные
        taxesPayable: '40000', // Задолженность по налогам
        otherCurrentLiabilities: '30000', // Прочие краткосрочные обязательства
      },
      
      // Долгосрочные активы (для расчета общего коэффициента ликвидности)
      longTermAssets: {
        nonCurrentAssets: '800000', // Внеоборотные активы
      },
      
      // Долгосрочные обязательства (для расчета общего коэффициента ликвидности)
      longTermLiabilities: {
        longTermLoans: '300000', // Долгосрочные заемные средства
        otherLongTermLiabilities: '100000', // Прочие долгосрочные обязательства
      },
      
      // Дополнительные настройки
      settings: {
        industry: 'manufacturing', // Отрасль (для сравнения с нормативами)
        companySize: 'medium', // Размер компании
        accountingStandard: 'ras', // Стандарт отчетности (RAS - РСБУ, IFRS - МСФО)
        includeDetailedAssets: true, // Включить детализацию активов
        includeDetailedLiabilities: true, // Включить детализацию обязательств
        includeBankruptcyRiskAssessment: true, // Включить оценку риска банкротства
        comparisonPeriod: 'current', // Период для сравнения (current, previous, custom)
        customPeriodName: 'Плановый период', // Название периода
      }
    });
    setResults(null);
    setErrors({});
    setSaveReportName('');
    setShowSaveDialog(false);
  };

  // Экспорт отчета в CSV
  const exportToCSV = () => {
    if (!results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки для CSV
    const headers = [
      'Показатель', 
      'Значение', 
      'Норматив по отрасли', 
      'Статус'
    ];

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    // Формируем строки для экспорта
    const rows = [
      ['Коэффициент текущей ликвидности', 
        formatCsvNumber(results.currentRatio), 
        formatCsvNumber(results.assessments.currentRatio.standard),
        results.assessments.currentRatio.meetsStandard ? 'Соответствует' : 'Не соответствует'
      ],
      ['Коэффициент быстрой ликвидности', 
        formatCsvNumber(results.quickRatio), 
        formatCsvNumber(results.assessments.quickRatio.standard),
        results.assessments.quickRatio.meetsStandard ? 'Соответствует' : 'Не соответствует'
      ],
      ['Коэффициент абсолютной ликвидности', 
        formatCsvNumber(results.cashRatio), 
        formatCsvNumber(results.assessments.cashRatio.standard),
        results.assessments.cashRatio.meetsStandard ? 'Соответствует' : 'Не соответствует'
      ],
      ['Коэффициент общей платежеспособности', 
        formatCsvNumber(results.generalSolvencyRatio), 
        '1,00',
        results.generalSolvencyRatio >= 1 ? 'Соответствует' : 'Не соответствует'
      ],
      ['Коэффициент обеспеченности собств. оборотными средствами', 
        formatCsvNumber(results.workingCapitalRatio), 
        '0,10',
        results.workingCapitalRatio >= 0.1 ? 'Соответствует' : 'Не соответствует'
      ],
      ['Доля оборотных средств в активах', 
        formatCsvNumber(results.currentAssetsToTotalAssetsRatio), 
        '-',
        '-'
      ]
    ];

    // Добавим информацию о балансе ликвидности
    const balanceRows = [
      [''],
      ['Анализ ликвидности баланса', '', '', ''],
      ['A1 ≥ П1', 
        `${formatCsvNumber(results.balanceLiquidity.a1)} ≥ ${formatCsvNumber(results.balanceLiquidity.l1)}`, 
        '', 
        results.balanceLiquidity.balanceCondition1 ? 'Выполняется' : 'Не выполняется'
      ],
      ['A2 ≥ П2', 
        `${formatCsvNumber(results.balanceLiquidity.a2)} ≥ ${formatCsvNumber(results.balanceLiquidity.l2)}`, 
        '', 
        results.balanceLiquidity.balanceCondition2 ? 'Выполняется' : 'Не выполняется'
      ],
      ['A3 ≥ П3', 
        `${formatCsvNumber(results.balanceLiquidity.a3)} ≥ ${formatCsvNumber(results.balanceLiquidity.l3)}`, 
        '', 
        results.balanceLiquidity.balanceCondition3 ? 'Выполняется' : 'Не выполняется'
      ],
      ['A4 ≤ П4', 
        `${formatCsvNumber(results.balanceLiquidity.a4)} ≤ ${formatCsvNumber(results.balanceLiquidity.l4)}`, 
        '', 
        results.balanceLiquidity.balanceCondition4 ? 'Выполняется' : 'Не выполняется'
      ],
      ['Ликвидность баланса:', 
        '', 
        '', 
        results.balanceLiquidity.absoluteLiquidity ? 'Абсолютная' : 'Недостаточная'
      ],
      [''],
      ['Риск банкротства (Z-модель Альтмана):', formatCsvNumber(results.zScore), '', results.bankruptcyRisk.description]
    ];

    // Добавляем основную информацию в начало файла
    const infoRows = [
      ['Отчет о коэффициентах ликвидности'],
      ['Дата расчета:', new Date().toLocaleDateString('ru-RU')],
      ['Отрасль:', industryTypes.find(i => i.id === results.industry)?.name || 'Не указана'],
      ['Размер компании:', companySizes.find(s => s.id === results.companySize)?.name || 'Не указан'],
      [''],
      ['Активы и пассивы'],
      ['Оборотные активы:', formatCsvNumber(results.totalCurrentAssets)],
      ['Краткосрочные обязательства:', formatCsvNumber(results.totalCurrentLiabilities)],
      ['Внеоборотные активы:', formatCsvNumber(results.totalLongTermAssets)],
      ['Долгосрочные обязательства:', formatCsvNumber(results.totalLongTermLiabilities)],
      ['Всего активов:', formatCsvNumber(results.totalAssets)],
      ['Всего обязательств:', formatCsvNumber(results.totalLiabilities)],
      [''],
    ].map(row => row.join(';'));

    // Формируем рекомендации
    const recommendationRows = [
      [''],
      ['Рекомендации:'],
      ...results.recommendations.map(rec => [rec.text, '', '', ''])
    ];

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [
      ...infoRows, 
      headers.join(';'), 
      ...rows.map(row => row.join(';')),
      ...balanceRows.map(row => row.join(';')),
      ...recommendationRows.map(row => row.join(';'))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'отчет_о_ликвидности.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения отчета
  const handleSaveReport = () => {
    if (showSaveDialog) {
      const savedResult = saveReport(saveReportName || null);
      if (savedResult) {
        alert(`Отчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveReportName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateLiquidityRatios();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <BarChart2 className="w-6 h-6 text-indigo-500" />
          Анализ ликвидности предприятия
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Секция оборотных активов */}
        <CurrentAssetsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Секция краткосрочных обязательств */}
        <CurrentLiabilitiesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Секция долгосрочных активов */}
        <LongTermAssetsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Секция долгосрочных обязательств */}
        <LongTermLiabilitiesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Настройки */}
        <SettingsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          industryTypes={industryTypes}
          companySizes={companySizes}
        />

        {/* Диалог сохранения отчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveReportName}
                onChange={(e) => setSaveReportName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveReport}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать коэффициенты'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<FileText className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Экспорт в CSV
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveReport}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить отчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;